import { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Bot, User, Loader2, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const KNOWLEDGE_BASE = {
  // Informações básicas
  'como funciona': 'O EstoqueProAI é um sistema completo de gestão de estoque que permite cadastrar produtos, controlar entradas e saídas, gerar relatórios financeiros, gerenciar vendas e muito mais! É uma solução completa para empresas que precisam organizar seu estoque.',
  
  // Gestão de produtos
  'cadastrar produto': 'Para cadastrar um produto, vá em "Gestão de Produtos" no menu lateral e clique em "Novo Produto". Preencha as informações como: nome, descrição, categoria, custo de aquisição, margem de lucro desejada, estoque inicial e código de barras (opcional).',
  'editar produto': 'Na página "Gestão de Produtos", clique no produto que deseja editar e use o botão de edição. Você pode atualizar todas as informações do produto.',
  'excluir produto': 'Na página "Gestão de Produtos", encontre o produto desejado e use a opção de exclusão. Produtos excluídos são marcados como inativos.',
  'produto': 'Produtos são os itens que sua empresa vende ou utiliza. Cada produto tem informações como nome, preço, estoque e categoria.',
  
  // Estoque
  'estoque': 'O estoque controla a quantidade de produtos disponíveis. Você pode registrar entradas (compras) e saídas (vendas) que atualizam automaticamente o estoque.',
  'estoque baixo': 'O sistema alerta automaticamente quando um produto está com estoque baixo (abaixo do estoque mínimo configurado). Você pode visualizar esses produtos destacados na página de Gestão de Produtos.',
  'entrada estoque': 'Para registrar entrada de estoque, vá em "Estoque" e clique em "Nova Movimentação". Selecione o tipo "Entrada", escolha o produto e informe a quantidade.',
  'saída estoque': 'As saídas de estoque são registradas automaticamente quando você faz uma venda na seção Financeiro. Você também pode registrar saídas manuais na página de Estoque.',
  'movimentação': 'Movimentações são registros de entrada ou saída de produtos no estoque. Cada movimentação tem uma data, quantidade e motivo.',
  
  // Relatórios financeiros
  'relatórios': 'Acesse "Relatórios Financeiros" para ver gráficos de receitas, despesas e lucros. O sistema mostra dados mensais, categorias de gastos e histórico completo de transações financeiras.',
  'receitas': 'Receitas são entradas de dinheiro, como vendas. Você pode registrar receitas na seção "Relatórios Financeiros" clicando em "Nova Movimentação" e selecionando tipo "Receita".',
  'despesas': 'Despesas são saídas de dinheiro, como compras, pagamentos e custos operacionais. Registre despesas em "Relatórios Financeiros" selecionando tipo "Despesa".',
  'lucro': 'O lucro é calculado automaticamente como receitas menos despesas. Você pode visualizar o lucro nos gráficos e relatórios financeiros.',
  
  // Pagamentos e assinatura
  'pagamento': 'Na seção "Pagamentos e Cobrança" você pode ver sua assinatura atual, histórico de pagamentos e escolher formas de pagamento: PIX (instantâneo), Boleto (até 3 dias úteis) ou Cartão de Crédito/Débito.',
  'assinatura': 'Você pode alterar seu plano na página "Pagamentos e Cobrança". O sistema oferece planos: Trial (14 dias grátis), Básico, Plus, Premium e Master, cada um com recursos específicos.',
  'pix': 'PIX é um método de pagamento instantâneo. Ao escolher PIX, você receberá um QR Code para escanear ou código para copiar e colar no aplicativo do seu banco.',
  'boleto': 'O boleto bancário pode levar até 3 dias úteis para ser confirmado. Após gerar o boleto, você pode fazer o download do PDF e pagar em qualquer banco ou lotérica.',
  'cartão': 'Pagamentos com cartão de crédito ou débito são processados de forma segura. Você pode salvar seu cartão para pagamentos recorrentes automáticos.',
  'fatura': 'Sua fatura aparece na página "Pagamentos e Cobrança". O sistema alerta quando a fatura está próxima do vencimento (10 dias antes) e quando está vencida.',
  
  // Funcionalidades gerais
  'categoria': 'Categorias ajudam a organizar seus produtos. Você pode criar categorias como: Eletrônicos, Roupas, Alimentos, etc. Acesse "Categorias" no menu para gerenciar.',
  'dashboard': 'O Dashboard mostra uma visão geral do seu negócio: produtos com estoque baixo, vendas recentes, receitas do mês e despesas. É a primeira página que você vê ao fazer login.',
  'configurações': 'Nas "Configurações" você pode atualizar seu perfil, alterar senha, gerenciar foto de perfil e configurar preferências da sua conta.',
  'usuários': 'Se você é administrador, pode gerenciar usuários da sua empresa na seção "Usuários". Você pode adicionar, editar ou remover usuários.',
  
  // Planos e recursos
  'planos': 'Nossos planos são: Trial (14 dias grátis), Básico (R$ 29,90/mês), Plus (R$ 59,90/mês), Premium (R$ 99,90/mês) e Master (R$ 149,90/mês). Cada plano oferece diferentes limites de produtos, usuários e recursos avançados.',
  'trial': 'O plano Trial oferece 14 dias grátis para você testar todas as funcionalidades do sistema. Após o período, você precisa escolher um dos planos disponíveis para continuar usando.',
  'preços': 'Nossos planos começam com Trial gratuito de 14 dias. Depois, temos planos Básico, Plus, Premium e Master com valores variados. Acesse "Pagamentos e Cobrança" para ver todos os planos e preços atualizados.',
  
  // Suporte e ajuda
  'ajuda': 'Estou aqui para ajudar! Você pode perguntar sobre: como funciona o sistema, como cadastrar produtos, como visualizar relatórios, como fazer pagamentos, planos disponíveis e muito mais. Se não conseguir resolver, posso encaminhar para nosso administrador.',
  'suporte': 'Se precisar de ajuda adicional ou tiver alguma dúvida específica, sua conversa será encaminhada para nosso administrador que entrará em contato em breve através do sistema.',
  'contato': 'Para entrar em contato, use o chatbot (este assistente). Se o bot não conseguir ajudar, sua conversa será automaticamente encaminhada para um administrador.',
  'login': 'Para fazer login, use seu email e senha cadastrados. Se esqueceu a senha, entre em contato com o suporte através do chatbot.',
  'senha': 'Se esqueceu sua senha, entre em contato com o suporte através do chatbot. Nossa equipe pode ajudar você a recuperar o acesso.',
  
  // Resposta padrão para perguntas não reconhecidas
  'default': 'Desculpe, não entendi sua pergunta completamente. Poderia reformular de outra forma? Posso ajudar com: gestão de produtos, estoque, relatórios financeiros, pagamentos, assinaturas e planos. Se precisar de ajuda mais específica, posso encaminhar para nosso administrador.'
};

// Palavras-chave suspeitas que podem indicar tentativas de acesso indevido
const SUSPICIOUS_KEYWORDS = [
  'código', 'código fonte', 'source code', 'github', 'repositório', 'repositorio',
  'senha admin', 'senha administrador', 'password admin', 'credencial', 'token',
  'api key', 'chave api', 'secret key', 'private key', 'backdoor', 'exploit',
  'sql injection', 'xss', 'vulnerabilidade', 'hack', 'bypass', 'crack',
  'database', 'banco de dados', 'schema', 'tabela', 'query sql',
  'root access', 'acesso root', 'permissão admin', 'admin access',
  'remover segurança', 'desabilitar segurança', 'bypass auth',
  'mostrar código', 'ver código', 'baixar código', 'copiar código'
];

// Função para detectar perguntas suspeitas
const isSuspiciousQuestion = (question) => {
  const lowerQuestion = question.toLowerCase();
  return SUSPICIOUS_KEYWORDS.some(keyword => lowerQuestion.includes(keyword));
};

export default function Chatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [needsEscalation, setNeedsEscalation] = useState(false);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [conversationStatus, setConversationStatus] = useState(null);
  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  // Buscar conversa não resolvida do usuário
  const fetchActiveConversation = async () => {
    if (!user?.id) return null;
    
    try {
      const { conversationsAPI } = await import('../api/conversations');
      // Usar rota específica para buscar conversas do próprio usuário
      const myConversations = await conversationsAPI.getMyConversations({ status: 'pending' });
      
      // Retornar a primeira conversa pendente (mais recente)
      return myConversations && myConversations.length > 0 ? myConversations[0] : null;
    } catch (error) {
      console.error('Erro ao buscar conversa:', error);
      return null;
    }
  };

  // Carregar conversa ao abrir
  useEffect(() => {
    if (isOpen) {
      loadConversation();
    } else {
      // Limpar intervalo quando fechar
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [isOpen, user?.id]);

  // Polling para tempo real quando há conversa pendente
  useEffect(() => {
    if (isOpen && currentConversationId && conversationStatus === 'pending') {
      // Limpar intervalo anterior se existir
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
      
      // Iniciar polling a cada 2 segundos para tempo real
      pollIntervalRef.current = setInterval(() => {
        checkForAdminReplies();
      }, 2000);
    } else {
      // Limpar intervalo quando não há conversa pendente
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [isOpen, currentConversationId, conversationStatus]);

  const loadConversation = async () => {
    if (!user?.id) {
      // Se não tem usuário, mostrar mensagem inicial
      setMessages([{
        id: 1,
        text: 'Olá! 👋 Sou o assistente virtual do EstoqueProAI. Como posso ajudá-lo hoje?',
        sender: 'bot',
        timestamp: new Date()
      }]);
      return;
    }

    const conversation = await fetchActiveConversation();
    
    if (conversation && conversation.messages) {
      // Carregar mensagens da conversa existente
      setCurrentConversationId(conversation.id);
      setConversationStatus(conversation.status);
      
      // Parse das mensagens
      let parsedMessages = [];
      try {
        parsedMessages = Array.isArray(conversation.messages) 
          ? conversation.messages 
          : typeof conversation.messages === 'string' 
            ? JSON.parse(conversation.messages)
            : [];
      } catch {
        parsedMessages = [];
      }

      // Converter para formato do componente
      const formattedMessages = parsedMessages.map((msg, index) => ({
        id: msg.id || index + 1,
        text: msg.text || msg.message || '',
        sender: msg.sender || 'bot',
        timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date(),
        adminName: msg.adminName
      }));

      setMessages(formattedMessages.length > 0 ? formattedMessages : [{
        id: 1,
        text: 'Olá! 👋 Sou o assistente virtual do EstoqueProAI. Como posso ajudá-lo hoje?',
        sender: 'bot',
        timestamp: new Date()
      }]);
    } else {
      // Nova conversa - mensagem inicial
      setCurrentConversationId(null);
      setConversationStatus(null);
      setMessages([{
        id: 1,
        text: 'Olá! 👋 Sou o assistente virtual do EstoqueProAI. Como posso ajudá-lo hoje?',
        sender: 'bot',
        timestamp: new Date()
      }]);
    }
  };

  const checkForAdminReplies = async () => {
    if (!currentConversationId || !user?.id) return;

    try {
      const { conversationsAPI } = await import('../api/conversations');
      const conversation = await conversationsAPI.getById(currentConversationId);
      
      if (!conversation || !conversation.messages) return;

      // Parse das mensagens
      let parsedMessages = [];
      try {
        parsedMessages = Array.isArray(conversation.messages) 
          ? conversation.messages 
          : typeof conversation.messages === 'string' 
            ? JSON.parse(conversation.messages)
            : [];
      } catch {
        parsedMessages = [];
      }

      // Formatear mensagens
      const formattedMessages = parsedMessages.map((msg, index) => ({
        id: msg.id || index + 1,
        text: msg.text || msg.message || '',
        sender: msg.sender || 'bot',
        timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date(),
        adminName: msg.adminName
      }));

      // Atualizar mensagens (sempre sincronizar com o backend)
      setMessages(prev => {
        // Verificar se há diferença no número de mensagens
        if (prev.length !== formattedMessages.length) {
          // Scroll para última mensagem quando houver novas
          setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
          }, 100);
          return formattedMessages;
        }
        // Verificar se há mudança no conteúdo (especialmente mensagens do admin)
        const prevAdminCount = prev.filter(m => m.sender === 'admin').length;
        const newAdminCount = formattedMessages.filter(m => m.sender === 'admin').length;
        
        if (newAdminCount > prevAdminCount) {
          setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
          }, 100);
          return formattedMessages;
        }
        
        return prev;
      });

      // Atualizar status
      if (conversation.status !== conversationStatus) {
        const previousStatus = conversationStatus;
        setConversationStatus(conversation.status);
        
        if (conversation.status === 'resolved' && previousStatus === 'pending') {
          // Conversa resolvida - mostrar mensagem e depois limpar e permitir nova conversa
          setMessages(formattedMessages => [...formattedMessages, {
            id: Date.now(),
            text: '✅ Esta conversa foi marcada como resolvida pelo administrador. Obrigado por entrar em contato! Você pode iniciar uma nova conversa agora.',
            sender: 'bot',
            timestamp: new Date()
          }]);
          
          // Aguardar 3 segundos e então limpar tudo para permitir nova conversa
          setTimeout(() => {
            setCurrentConversationId(null);
            setConversationStatus(null);
            setNeedsEscalation(false);
            setMessages([{
              id: 1,
              text: 'Olá! 👋 Sou o assistente virtual do EstoqueProAI. Como posso ajudá-lo hoje?',
              sender: 'bot',
              timestamp: new Date()
            }]);
          }, 3000);
        }
      }
    } catch (error) {
      console.error('Erro ao verificar respostas do admin:', error);
    }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const findAnswer = (question) => {
    const lowerQuestion = question.toLowerCase();
    
    // Verificar se é uma pergunta suspeita (tentativa de acesso indevido)
    if (isSuspiciousQuestion(question)) {
      return 'Desculpe, não posso fornecer informações sobre código-fonte, credenciais de acesso, vulnerabilidades de segurança ou qualquer informação técnica sensível do sistema. Se você precisa de ajuda com alguma funcionalidade específica, posso orientá-lo sobre como usar o sistema. Se sua intenção não é essa, sua conversa pode ser encaminhada para nosso administrador.';
    }
    
    // Verificar se a pergunta contém alguma palavra-chave (busca mais inteligente)
    // Ordenar por relevância (palavras-chave mais específicas primeiro)
    const sortedKeys = Object.keys(KNOWLEDGE_BASE)
      .filter(key => key !== 'default')
      .sort((a, b) => b.length - a.length); // Palavras mais longas primeiro
    
    for (const key of sortedKeys) {
      if (lowerQuestion.includes(key)) {
        return KNOWLEDGE_BASE[key];
      }
    }
    
    // Verificar palavras-chave parciais (mais flexível)
    for (const [key, answer] of Object.entries(KNOWLEDGE_BASE)) {
      if (key === 'default') continue;
      
      const keyWords = key.split(' ');
      // Se pelo menos 2 palavras da chave estão na pergunta
      if (keyWords.length > 1) {
        const matches = keyWords.filter(word => 
          word.length > 3 && lowerQuestion.includes(word)
        );
        if (matches.length >= 2) {
          return answer;
        }
      }
    }
    
    return null;
  };

  const handleSend = async () => {
    if (!input.trim() || isTyping) return;

    // Se já tem conversa pendente, adicionar mensagem diretamente à conversa
    if (currentConversationId && conversationStatus === 'pending') {
      const userMessage = {
        id: Date.now(),
        text: input,
        sender: 'user',
        timestamp: new Date()
      };

      // Adicionar mensagem localmente imediatamente
      setMessages(prev => [...prev, userMessage]);
      const messageText = input;
      setInput('');
      setIsTyping(true);

      try {
        // Salvar mensagem no backend
        const { conversationsAPI } = await import('../api/conversations');
        await conversationsAPI.addMessage(currentConversationId, messageText);
        
        setIsTyping(false);
      } catch (error) {
        console.error('Erro ao enviar mensagem:', error);
        setIsTyping(false);
        // Remover mensagem se falhou
        setMessages(prev => prev.filter(m => m.id !== userMessage.id));
      }
      return;
    }

    // Comportamento normal quando não há conversa pendente
    const userMessage = {
      id: Date.now(),
      text: input,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const userInput = input;
    setInput('');
    setIsTyping(true);

    // Simular delay de resposta do bot
    setTimeout(() => {
      const answer = findAnswer(userInput);
      
      if (answer) {
        setMessages(prev => [...prev, {
          id: Date.now() + 1,
          text: answer,
          sender: 'bot',
          timestamp: new Date()
        }]);
      } else {
        // Se não encontrou resposta, perguntar se quer encaminhar
        setMessages(prev => [...prev, {
          id: Date.now() + 1,
          text: 'Desculpe, não consegui entender sua pergunta completamente. Gostaria que eu encaminhasse sua dúvida para nosso administrador? Ele entrará em contato em breve.',
          sender: 'bot',
          timestamp: new Date(),
          needsEscalation: true
        }]);
        setNeedsEscalation(true);
      }
      
      setIsTyping(false);
    }, 1000);
  };

  const handleEscalate = async () => {
    try {
      // Importar API de conversas
      const { conversationsAPI } = await import('../api/conversations');
      
      // Criar nova conversa sempre (não reutilizar)
      const newConversation = await conversationsAPI.create(messages);
      const conversationId = newConversation.id;
      setCurrentConversationId(conversationId);
      setConversationStatus('pending');
      
      setMessages(prev => [...prev, {
        id: Date.now(),
        text: '✅ Sua conversa foi encaminhada para nosso administrador! Você pode continuar conversando aqui. As respostas do administrador aparecerão em tempo real.',
        sender: 'bot',
        timestamp: new Date()
      }]);

      // Limpar estado de escalação e desabilitar input
      setNeedsEscalation(false);
      
      // Iniciar polling mais frequente para tempo real
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
      pollIntervalRef.current = setInterval(() => {
        checkForAdminReplies();
      }, 2000); // 2 segundos para tempo real
      
      // Se for master admin, pode redirecionar para página de conversas
      if (user?.email === 'adm@revicont.dev' || (user?.role === 'admin' && !user?.companyId)) {
        setTimeout(() => {
          navigate('/super-admin/conversations');
        }, 2000);
      }
    } catch (error) {
      console.error('Erro ao encaminhar conversa:', error);
      setMessages(prev => [...prev, {
        id: Date.now(),
        text: '❌ Erro ao encaminhar conversa. Por favor, tente novamente ou entre em contato diretamente pelo suporte.',
        sender: 'bot',
        timestamp: new Date()
      }]);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 w-14 h-14 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-full shadow-2xl hover:shadow-3xl flex items-center justify-center transition-all hover:scale-110 z-50"
        aria-label="Abrir chatbot"
      >
        <MessageCircle className="w-6 h-6" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 w-96 h-[600px] bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border-2 border-gray-200 dark:border-gray-700 flex flex-col z-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-4 rounded-t-2xl flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
            <Bot className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-white font-bold">Assistente Virtual</h3>
            <p className="text-blue-100 text-xs">EstoqueProAI</p>
          </div>
        </div>
        <button
          onClick={() => {
            // Não limpar mensagens ao fechar - apenas esconder o chat
            // Só limpará quando a conversa for resolvida
            setIsOpen(false);
            setNeedsEscalation(false);
          }}
          className="text-white hover:bg-white/20 rounded-full p-1 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[80%] rounded-2xl p-3 ${
                message.sender === 'user'
                  ? 'bg-blue-500 text-white'
                  : message.sender === 'admin'
                  ? 'bg-green-500 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
              }`}
            >
              <div className="flex items-start gap-2">
                {message.sender === 'bot' && (
                  <Bot className="w-4 h-4 mt-0.5 flex-shrink-0" />
                )}
                {message.sender === 'admin' && (
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-green-500" />
                )}
                <div className="flex-1">
                  {message.sender === 'admin' && message.adminName && (
                    <p className="text-xs font-semibold text-green-600 dark:text-green-400 mb-1">
                      {message.adminName}
                    </p>
                  )}
                  <p className="text-sm whitespace-pre-wrap">{message.text}</p>
                  {message.needsEscalation && (
                    <button
                      onClick={handleEscalate}
                      className="mt-2 px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-xs font-semibold transition-colors"
                    >
                      Sim, encaminhar para administrador
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
        
        {isTyping && (
          <div className="flex justify-start">
            <div className="bg-gray-100 dark:bg-gray-700 rounded-2xl p-3 flex items-center gap-2">
              <Bot className="w-4 h-4" />
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm text-gray-600 dark:text-gray-400">Digitando...</span>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-700">
        {currentConversationId && conversationStatus === 'pending' && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-3">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-blue-700 dark:text-blue-300">
                💬 Conversando com administrador - respostas em tempo real
              </p>
            </div>
          </div>
        )}
        <div className="flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={currentConversationId && conversationStatus === 'pending' 
              ? "Digite sua mensagem para o administrador..." 
              : "Digite sua pergunta..."}
            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={isTyping}
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || isTyping}
            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
          {currentConversationId && conversationStatus === 'pending' 
            ? '💬 Chat ao vivo com administrador ativo'
            : '💡 Dica: Pergunte sobre funcionalidades, como usar o sistema, planos e mais!'}
        </p>
      </div>
    </div>
  );
}

